import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import {
  Box, Typography, Tabs, Tab, Button, TextField, Card, CardContent,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Grid, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { FONT_FAMILY } from '../common/sharedFunctions';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ p: 2 }}>{children}</Box> : null;
}

export default function CommunityManagement() {
  const dispatch = useDispatch();
  const {
    fetchAnnouncements, addAnnouncement, deleteAnnouncement,
    fetchAcademyContent, addAcademyCategory, deleteAcademyCategory,
    fetchTrafficReports, deleteTrafficReport,
    fetchFoodSpots, addFoodSpot, deleteFoodSpot,
    fetchLostFound, updateLostFoundStatus, deleteLostFoundItem,
    fetchEmergencyAlerts, resolveEmergencyAlert,
    fetchPolls, addPoll, deletePoll,
    fetchCommunityChat, deleteCommunityMessage, pinCommunityMessage,
    fetchMarketplace, deleteMarketplaceItem,
    fetchEvents, addEvent, deleteEvent,
    fetchAllDriverReputations, REPUTATION_BADGES,
  } = api;

  const community = useSelector(state => state.communitydata);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    dispatch(fetchAnnouncements());
    dispatch(fetchAcademyContent());
    dispatch(fetchTrafficReports());
    dispatch(fetchFoodSpots());
    dispatch(fetchLostFound());
    dispatch(fetchEmergencyAlerts());
    dispatch(fetchPolls());
    dispatch(fetchCommunityChat());
    dispatch(fetchMarketplace());
    dispatch(fetchEvents());
    dispatch(fetchAllDriverReputations());
  }, []);

  // Dialog states
  const [announcementDialog, setAnnouncementDialog] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({ title: '', body: '' });
  const [academyDialog, setAcademyDialog] = useState(false);
  const [academyForm, setAcademyForm] = useState({ title: '', icon: '', items: '' });
  const [foodDialog, setFoodDialog] = useState(false);
  const [foodForm, setFoodForm] = useState({ name: '', category: '', price: '', notes: '', rating: '' });
  const [pollDialog, setPollDialog] = useState(false);
  const [pollForm, setPollForm] = useState({ question: '', options: '' });
  const [eventDialog, setEventDialog] = useState(false);
  const [eventForm, setEventForm] = useState({ title: '', description: '', location: '', type: '', eventDate: '' });

  const handleAddAnnouncement = () => {
    if (!announcementForm.title) return;
    dispatch(addAnnouncement(announcementForm));
    setAnnouncementForm({ title: '', body: '' });
    setAnnouncementDialog(false);
  };

  const handleAddAcademy = () => {
    if (!academyForm.title) return;
    dispatch(addAcademyCategory({ ...academyForm, items: academyForm.items.split('\n').filter(i => i.trim()) }));
    setAcademyForm({ title: '', icon: '', items: '' });
    setAcademyDialog(false);
  };

  const handleAddFood = () => {
    if (!foodForm.name) return;
    dispatch(addFoodSpot({ ...foodForm, price: parseFloat(foodForm.price) || 0, rating: parseFloat(foodForm.rating) || 0 }));
    setFoodForm({ name: '', category: '', price: '', notes: '', rating: '' });
    setFoodDialog(false);
  };

  const handleAddPoll = () => {
    if (!pollForm.question || !pollForm.options) return;
    dispatch(addPoll({ question: pollForm.question, options: pollForm.options.split('\n').filter(o => o.trim()) }));
    setPollForm({ question: '', options: '' });
    setPollDialog(false);
  };

  const handleAddEvent = () => {
    if (!eventForm.title) return;
    dispatch(addEvent({ ...eventForm, eventDate: eventForm.eventDate ? new Date(eventForm.eventDate).getTime() : null }));
    setEventForm({ title: '', description: '', location: '', type: '', eventDate: '' });
    setEventDialog(false);
  };

  const timeAgo = (ts) => {
    if (!ts) return '';
    const diff = Math.floor((Date.now() - ts) / 60000);
    if (diff < 60) return diff + ' min ago';
    if (diff < 1440) return Math.floor(diff / 60) + ' hr ago';
    return Math.floor(diff / 1440) + ' days ago';
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ fontFamily: FONT_FAMILY, fontWeight: 'bold', mb: 2 }}>
        🏘️ Community Management
      </Typography>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
        <Tab label="📢 Announcements" />
        <Tab label="💬 Chat" />
        <Tab label="🎓 Academy" />
        <Tab label="🚦 Traffic" />
        <Tab label="🍽 Food" />
        <Tab label="🔍 Lost & Found" />
        <Tab label="🚨 Emergency" />
        <Tab label="📊 Polls" />
        <Tab label="🛒 Marketplace" />
        <Tab label="📅 Events" />
        <Tab label="🏅 Reputation" />
      </Tabs>

      {/* ANNOUNCEMENTS */}
      <TabPanel value={tab} index={0}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAnnouncementDialog(true)} sx={{ mb: 2 }}>Add Announcement</Button>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow><TableCell>Title</TableCell><TableCell>Body</TableCell><TableCell>Time</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {[...(community?.announcements || [])].reverse().map(item => (
                <TableRow key={item.id}>
                  <TableCell>{item.title}</TableCell>
                  <TableCell>{item.body?.substring(0, 80)}</TableCell>
                  <TableCell>{timeAgo(item.createdAt)}</TableCell>
                  <TableCell><IconButton onClick={() => dispatch(deleteAnnouncement(item.id))}><DeleteIcon color="error" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Dialog open={announcementDialog} onClose={() => setAnnouncementDialog(false)} fullWidth>
          <DialogTitle>New Announcement</DialogTitle>
          <DialogContent>
            <TextField fullWidth label="Title" value={announcementForm.title} onChange={e => setAnnouncementForm({ ...announcementForm, title: e.target.value })} sx={{ mt: 1, mb: 2 }} />
            <TextField fullWidth label="Body" multiline rows={3} value={announcementForm.body} onChange={e => setAnnouncementForm({ ...announcementForm, body: e.target.value })} />
          </DialogContent>
          <DialogActions><Button onClick={() => setAnnouncementDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleAddAnnouncement}>Post</Button></DialogActions>
        </Dialog>
      </TabPanel>

      {/* CHAT MODERATION */}
      <TabPanel value={tab} index={1}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>Moderate community chat messages. Pin or delete messages.</Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow><TableCell>Sender</TableCell><TableCell>Message</TableCell><TableCell>Time</TableCell><TableCell>Pinned</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {[...(community?.chat || [])].reverse().slice(0, 50).map(msg => (
                <TableRow key={msg.id}>
                  <TableCell>{msg.senderName}</TableCell>
                  <TableCell>{msg.text?.substring(0, 60) || (msg.type === 'image' ? '📷 Image' : '')}</TableCell>
                  <TableCell>{timeAgo(msg.createdAt)}</TableCell>
                  <TableCell>{msg.pinned ? <Chip label="Pinned" size="small" color="warning" /> : '-'}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => dispatch(pinCommunityMessage(msg.id, !msg.pinned))}>{msg.pinned ? 'Unpin' : 'Pin'}</Button>
                    <IconButton onClick={() => dispatch(deleteCommunityMessage(msg.id))}><DeleteIcon color="error" fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* ACADEMY */}
      <TabPanel value={tab} index={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAcademyDialog(true)} sx={{ mb: 2 }}>Add Category</Button>
        <Grid container spacing={2}>
          {(community?.academy || []).map(cat => (
            <Grid item xs={12} md={6} key={cat.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">{cat.icon} {cat.title}</Typography>
                    <IconButton onClick={() => dispatch(deleteAcademyCategory(cat.id))}><DeleteIcon color="error" /></IconButton>
                  </Box>
                  {(cat.items || []).map((item, i) => <Typography key={i} variant="body2" sx={{ ml: 2 }}>✔ {typeof item === 'string' ? item : item.text}</Typography>)}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Dialog open={academyDialog} onClose={() => setAcademyDialog(false)} fullWidth>
          <DialogTitle>Add Academy Category</DialogTitle>
          <DialogContent>
            <TextField fullWidth label="Title" value={academyForm.title} onChange={e => setAcademyForm({ ...academyForm, title: e.target.value })} sx={{ mt: 1, mb: 2 }} />
            <TextField fullWidth label="Icon (emoji)" value={academyForm.icon} onChange={e => setAcademyForm({ ...academyForm, icon: e.target.value })} sx={{ mb: 2 }} />
            <TextField fullWidth label="Items (one per line)" multiline rows={5} value={academyForm.items} onChange={e => setAcademyForm({ ...academyForm, items: e.target.value })} />
          </DialogContent>
          <DialogActions><Button onClick={() => setAcademyDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleAddAcademy}>Add</Button></DialogActions>
        </Dialog>
      </TabPanel>

      {/* TRAFFIC */}
      <TabPanel value={tab} index={3}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>Driver-reported traffic updates</Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow><TableCell>Type</TableCell><TableCell>Reporter</TableCell><TableCell>Location</TableCell><TableCell>Time</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {[...(community?.trafficReports || [])].reverse().map(r => (
                <TableRow key={r.id}>
                  <TableCell>{r.icon} {r.label}</TableCell>
                  <TableCell>{r.driverName}</TableCell>
                  <TableCell>{r.lat?.toFixed(4)}, {r.lng?.toFixed(4)}</TableCell>
                  <TableCell>{timeAgo(r.createdAt)}</TableCell>
                  <TableCell><IconButton onClick={() => dispatch(deleteTrafficReport(r.id))}><DeleteIcon color="error" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* FOOD */}
      <TabPanel value={tab} index={4}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFoodDialog(true)} sx={{ mb: 2 }}>Add Food Spot</Button>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Category</TableCell><TableCell>Price</TableCell><TableCell>Rating</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {(community?.foodSpots || []).map(s => (
                <TableRow key={s.id}>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>{s.category}</TableCell>
                  <TableCell>₱{s.price}</TableCell>
                  <TableCell>{'⭐'.repeat(Math.round(s.rating || 0))}</TableCell>
                  <TableCell><IconButton onClick={() => dispatch(deleteFoodSpot(s.id))}><DeleteIcon color="error" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Dialog open={foodDialog} onClose={() => setFoodDialog(false)} fullWidth>
          <DialogTitle>Add Food Spot</DialogTitle>
          <DialogContent>
            <TextField fullWidth label="Name" value={foodForm.name} onChange={e => setFoodForm({ ...foodForm, name: e.target.value })} sx={{ mt: 1, mb: 2 }} />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Category</InputLabel>
              <Select value={foodForm.category} label="Category" onChange={e => setFoodForm({ ...foodForm, category: e.target.value })}>
                {['Breakfast', 'Lunch', 'Dinner', 'Snacks', 'Coffee', '24-Hour', 'Carinderia', 'Fast Food'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Price (₱)" type="number" value={foodForm.price} onChange={e => setFoodForm({ ...foodForm, price: e.target.value })} sx={{ mb: 2 }} />
            <TextField fullWidth label="Rating (1-5)" type="number" value={foodForm.rating} onChange={e => setFoodForm({ ...foodForm, rating: e.target.value })} sx={{ mb: 2 }} />
            <TextField fullWidth label="Notes" value={foodForm.notes} onChange={e => setFoodForm({ ...foodForm, notes: e.target.value })} />
          </DialogContent>
          <DialogActions><Button onClick={() => setFoodDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleAddFood}>Add</Button></DialogActions>
        </Dialog>
      </TabPanel>

      {/* LOST & FOUND */}
      <TabPanel value={tab} index={5}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow><TableCell>Item</TableCell><TableCell>Description</TableCell><TableCell>Driver</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {[...(community?.lostFound || [])].reverse().map(item => (
                <TableRow key={item.id}>
                  <TableCell>{item.itemName}</TableCell>
                  <TableCell>{item.description?.substring(0, 40)}</TableCell>
                  <TableCell>{item.driverName}</TableCell>
                  <TableCell>
                    <Chip label={item.status} size="small" color={item.status === 'claimed' ? 'success' : item.status === 'reported' ? 'warning' : 'error'} />
                  </TableCell>
                  <TableCell>
                    {item.status === 'reported' && <Button size="small" onClick={() => dispatch(updateLostFoundStatus(item.id, 'claimed'))}>Mark Claimed</Button>}
                    <IconButton onClick={() => dispatch(deleteLostFoundItem(item.id))}><DeleteIcon color="error" fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* EMERGENCY */}
      <TabPanel value={tab} index={6}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>Active emergency alerts from drivers</Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow><TableCell>Type</TableCell><TableCell>Driver</TableCell><TableCell>Phone</TableCell><TableCell>Vehicle</TableCell><TableCell>Status</TableCell><TableCell>Time</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {[...(community?.emergencyAlerts || [])].reverse().map(alert => (
                <TableRow key={alert.id} sx={{ backgroundColor: alert.status === 'active' ? '#fef2f2' : 'inherit' }}>
                  <TableCell><Chip label={alert.type === 'sos' ? '🚨 SOS' : alert.label || alert.type} size="small" color={alert.status === 'active' ? 'error' : 'default'} /></TableCell>
                  <TableCell>{alert.driverName}</TableCell>
                  <TableCell>{alert.phone}</TableCell>
                  <TableCell>{alert.vehicleNumber}</TableCell>
                  <TableCell><Chip label={alert.status} size="small" color={alert.status === 'active' ? 'error' : 'success'} /></TableCell>
                  <TableCell>{timeAgo(alert.createdAt)}</TableCell>
                  <TableCell>
                    {alert.status === 'active' && <Button size="small" color="success" onClick={() => dispatch(resolveEmergencyAlert(alert.id))}>Resolve</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* POLLS */}
      <TabPanel value={tab} index={7}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPollDialog(true)} sx={{ mb: 2 }}>Create Poll</Button>
        <Grid container spacing={2}>
          {(community?.polls || []).map(poll => {
            const total = poll.votes ? Object.keys(poll.votes).length : 0;
            return (
              <Grid item xs={12} md={6} key={poll.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1" fontWeight="bold">{poll.question}</Typography>
                      <IconButton onClick={() => dispatch(deletePoll(poll.id))}><DeleteIcon color="error" fontSize="small" /></IconButton>
                    </Box>
                    {(poll.options || []).map((opt, idx) => {
                      const count = poll.votes ? Object.values(poll.votes).filter(v => v === idx).length : 0;
                      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                      return <Typography key={idx} variant="body2">{opt} — {pct}% ({count})</Typography>;
                    })}
                    <Typography variant="caption" color="text.secondary">{total} total votes</Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        <Dialog open={pollDialog} onClose={() => setPollDialog(false)} fullWidth>
          <DialogTitle>Create Poll</DialogTitle>
          <DialogContent>
            <TextField fullWidth label="Question" value={pollForm.question} onChange={e => setPollForm({ ...pollForm, question: e.target.value })} sx={{ mt: 1, mb: 2 }} />
            <TextField fullWidth label="Options (one per line)" multiline rows={4} value={pollForm.options} onChange={e => setPollForm({ ...pollForm, options: e.target.value })} />
          </DialogContent>
          <DialogActions><Button onClick={() => setPollDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleAddPoll}>Create</Button></DialogActions>
        </Dialog>
      </TabPanel>

      {/* MARKETPLACE */}
      <TabPanel value={tab} index={8}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>Driver marketplace listings</Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow><TableCell>Item</TableCell><TableCell>Price</TableCell><TableCell>Seller</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {[...(community?.marketplace || [])].reverse().map(item => (
                <TableRow key={item.id}>
                  <TableCell>{item.title}</TableCell>
                  <TableCell>₱{item.price}</TableCell>
                  <TableCell>{item.sellerName}</TableCell>
                  <TableCell><Chip label={item.status} size="small" /></TableCell>
                  <TableCell><IconButton onClick={() => dispatch(deleteMarketplaceItem(item.id))}><DeleteIcon color="error" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* EVENTS */}
      <TabPanel value={tab} index={9}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEventDialog(true)} sx={{ mb: 2 }}>Add Event</Button>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow><TableCell>Title</TableCell><TableCell>Type</TableCell><TableCell>Location</TableCell><TableCell>Date</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {(community?.events || []).map(ev => (
                <TableRow key={ev.id}>
                  <TableCell>{ev.title}</TableCell>
                  <TableCell>{ev.type}</TableCell>
                  <TableCell>{ev.location}</TableCell>
                  <TableCell>{ev.eventDate ? new Date(ev.eventDate).toLocaleDateString() : 'TBA'}</TableCell>
                  <TableCell><IconButton onClick={() => dispatch(deleteEvent(ev.id))}><DeleteIcon color="error" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Dialog open={eventDialog} onClose={() => setEventDialog(false)} fullWidth>
          <DialogTitle>Add Event</DialogTitle>
          <DialogContent>
            <TextField fullWidth label="Title" value={eventForm.title} onChange={e => setEventForm({ ...eventForm, title: e.target.value })} sx={{ mt: 1, mb: 2 }} />
            <TextField fullWidth label="Description" multiline rows={2} value={eventForm.description} onChange={e => setEventForm({ ...eventForm, description: e.target.value })} sx={{ mb: 2 }} />
            <TextField fullWidth label="Location" value={eventForm.location} onChange={e => setEventForm({ ...eventForm, location: e.target.value })} sx={{ mb: 2 }} />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Type</InputLabel>
              <Select value={eventForm.type} label="Type" onChange={e => setEventForm({ ...eventForm, type: e.target.value })}>
                {['Safety Seminar', 'Meetup', 'Training', 'Partner Promo', 'Other'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Date" type="datetime-local" value={eventForm.eventDate} onChange={e => setEventForm({ ...eventForm, eventDate: e.target.value })} InputLabelProps={{ shrink: true }} />
          </DialogContent>
          <DialogActions><Button onClick={() => setEventDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleAddEvent}>Add</Button></DialogActions>
        </Dialog>
      </TabPanel>

      {/* REPUTATION BADGES */}
      <TabPanel value={tab} index={10}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>Driver reputation badges — drivers self-select these so riders can choose based on needs.</Typography>
        <Grid container spacing={1} sx={{ mb: 3 }}>
          {(REPUTATION_BADGES || []).map(b => (
            <Grid item key={b.key}><Chip label={b.label} size="small" variant="outlined" /></Grid>
          ))}
        </Grid>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow><TableCell>Driver</TableCell><TableCell>Badges</TableCell></TableRow></TableHead>
            <TableBody>
              {(community?.allDriverReputations || []).map(driver => (
                <TableRow key={driver.uid}>
                  <TableCell>{driver.firstName} {driver.lastName}</TableCell>
                  <TableCell>
                    {Object.entries(driver.reputationBadges || {}).filter(([, v]) => v).map(([key]) => {
                      const badge = (REPUTATION_BADGES || []).find(b => b.key === key);
                      return badge ? <Chip key={key} label={badge.label} size="small" sx={{ mr: 0.5, mb: 0.5 }} /> : null;
                    })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>
    </Box>
  );
}
